import { useCallback, useMemo, useState } from 'react';

import type { DateValue } from 'react-aria-components/Calendar';
import type { DateRange } from 'react-aria-components/DateRangePicker';
import {
  RangeCalendar as RACRangeCalendar,
  type RangeCalendarProps as RACRangeCalendarProps,
} from 'react-aria-components/RangeCalendar';

import { cx } from '../../utils/cx';
import { getFutureDateRangePresets } from '../../utils/get_future_date_range_presets';
import { getIsDateRangesEqual } from '../../utils/get_is_date_ranges_equal';
import { getPastDateRangePresets } from '../../utils/get_past_date_range_presets';
import { Button } from '../button/button';
import { useDateFormat } from '../date_format_context/date_format_context';
import { CalendarContent } from './calendar_content/calendar_content';

import calendarStyles from './calendar_content/calendar_content.module.css';
import styles from './range_calendar.module.css';

export type RangeCalendarProps = Omit<
  RACRangeCalendarProps<DateValue>,
  'className' | 'onChange' | 'defaultValue'
> & {
  className?: string;
  value?: DateRange | null;
  defaultValue?: DateRange | null;
  onChange?: (value: DateRange) => void;
  presets?: 'past' | 'future';
};

type FocusChangeValue = Parameters<
  NonNullable<RangeCalendarProps['onFocusChange']>
>[0];

function getSelectedPresetKey(
  currentValue: DateRange | null,
  presets: ReturnType<typeof getPastDateRangePresets>,
): string | null {
  if (!currentValue) {
    return null;
  }

  const selectedPreset = presets.find((preset) =>
    getIsDateRangesEqual(currentValue, preset.value),
  );

  return selectedPreset?.key ?? null;
}

export function RangeCalendar({
  className,
  defaultValue,
  value,
  onChange,
  defaultFocusedValue,
  focusedValue,
  onFocusChange,
  presets = 'past',
  firstDayOfWeek,
  ...rest
}: RangeCalendarProps) {
  const { firstDayOfWeek: contextFirstDayOfWeek } = useDateFormat();
  const isValueControlled = value !== undefined;
  const isFocusedControlled = focusedValue !== undefined;

  const [uncontrolledValue, setUncontrolledValue] = useState<DateRange | null>(
    () => defaultValue ?? null,
  );

  const currentValue = isValueControlled ? (value ?? null) : uncontrolledValue;

  const [uncontrolledFocusedValue, setUncontrolledFocusedValue] =
    useState<DateValue | null>(() => {
      if (defaultFocusedValue !== undefined) {
        return defaultFocusedValue ?? null;
      }
      const initialValue = value ?? defaultValue;
      return initialValue?.start ?? initialValue?.end ?? null;
    });

  const currentFocusedValue = isFocusedControlled
    ? (focusedValue ?? null)
    : uncontrolledFocusedValue;

  const handleValueChange = useCallback(
    (nextValue: DateRange) => {
      if (!isValueControlled) {
        setUncontrolledValue(nextValue);
      }
      if (!isFocusedControlled) {
        setUncontrolledFocusedValue(nextValue.start);
      }
      onChange?.(nextValue);
    },
    [isFocusedControlled, isValueControlled, onChange],
  );

  const handleFocusChange = useCallback(
    (nextFocusedValue: FocusChangeValue) => {
      if (!isFocusedControlled) {
        setUncontrolledFocusedValue(nextFocusedValue);
      }
      onFocusChange?.(nextFocusedValue);
    },
    [isFocusedControlled, onFocusChange],
  );

  const activePresets = useMemo(
    () =>
      presets === 'future'
        ? getFutureDateRangePresets()
        : getPastDateRangePresets(),
    [presets],
  );
  const selectedPresetKey = useMemo(
    () => getSelectedPresetKey(currentValue, activePresets),
    [currentValue, activePresets],
  );

  return (
    <div className={cx([styles.range_calendar, className])}>
      <div className={styles.presets_column}>
        {activePresets.map((preset) => (
          <Button
            key={preset.key}
            size="s"
            variant="tertiary"
            className={cx([
              styles.preset_button,
              {
                [styles.preset_button_selected]:
                  selectedPresetKey === preset.key,
              },
            ])}
            onPress={() => handleValueChange(preset.value)}
          >
            {preset.label}
          </Button>
        ))}
      </div>
      <RACRangeCalendar
        {...rest}
        firstDayOfWeek={firstDayOfWeek ?? contextFirstDayOfWeek}
        value={currentValue ?? undefined}
        onChange={handleValueChange}
        focusedValue={currentFocusedValue ?? undefined}
        onFocusChange={handleFocusChange}
        className={cx([calendarStyles.calendar, calendarStyles.range_mode])}
      >
        <CalendarContent />
      </RACRangeCalendar>
    </div>
  );
}
